import React, { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Upload, ImageIcon, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

/**
 * Upload logo del locale. Lato client converte l'immagine in data-URL e
 * la ridimensiona a max 256×256 px (preservando l'aspect ratio) per
 * mantenere la dimensione finale ~30-80 KB. La stringa risultante viene
 * passata al parent via `onChange(dataUrl)` e salvata nel campo `logo`
 * del locale. Per rimuovere il logo, il parent passa `value=null` e
 * mostriamo il placeholder.
 *
 * Props:
 *   value: string | null  - data-URL o URL corrente del logo
 *   onChange: (newValue: string | null) => void
 *   maxSizeMB: number     - dimensione massima del file di input (default 2 MB)
 */
const MAX_DIM = 256;
const OUTPUT_QUALITY = 0.85;

const resizeImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Impossibile leggere il file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Formato immagine non valido"));
      img.onload = () => {
        const { width, height } = img;
        const scale = Math.min(MAX_DIM / width, MAX_DIM / height, 1);
        const w = Math.round(width * scale);
        const h = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        // Output JPEG (più leggero) o PNG se l'originale lo era e ha trasparenza
        const fmt = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const dataUrl = canvas.toDataURL(fmt, OUTPUT_QUALITY);
        resolve(dataUrl);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const LogoUpload = ({ value, onChange, maxSizeMB = 2 }) => {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Carica un file immagine (PNG, JPG, WEBP)');
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Immagine troppo grande (max ${maxSizeMB} MB)`);
      return;
    }
    setLoading(true);
    try {
      const dataUrl = await resizeImage(file);
      onChange(dataUrl);
    } catch (e) {
      toast.error(e.message || "Errore durante l'elaborazione dell'immagine");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    onChange(null);
  };

  return (
    <div className="flex items-start gap-3" data-testid="logo-upload">
      <div
        className={cn(
          'w-20 h-20 rounded-xl border-2 flex items-center justify-center overflow-hidden shrink-0',
          value ? 'border-primary bg-background' : 'border-dashed border-muted-foreground/40 bg-muted'
        )}
      >
        {value ? (
          <img src={value} alt="Logo locale" className="w-full h-full object-cover" data-testid="logo-preview" />
        ) : (
          <ImageIcon className="w-7 h-7 text-muted-foreground/60" />
        )}
      </div>
      <div className="flex flex-col gap-2 min-w-0">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={handleFile}
          data-testid="logo-file-input"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={loading}
          data-testid="logo-upload-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          {value ? 'Sostituisci' : 'Carica logo'}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            className="text-destructive hover:text-destructive"
            data-testid="logo-remove-btn"
          >
            <X className="w-4 h-4 mr-2" /> Rimuovi
          </Button>
        )}
        <p className="text-[11px] text-muted-foreground">
          PNG / JPG / WEBP, max {maxSizeMB} MB. Ridimensionato a {MAX_DIM}px.
        </p>
      </div>
    </div>
  );
};

export default LogoUpload;
